import { accessMapping } from '@constants';

interface RouteMenuItem {
  name: string;
  to: string;
  icon: JSX.Element;
  external?: never;
  /**
   * Exact roles for this item, when substring matching against `accessMapping` gets it wrong.
   *
   * The default below asks whether any of the role's allowed path fragments appears *anywhere* in
   * the item's URL. That looseness makes `/incorporations` visible to kyc and support, who are
   * allowed `'incorporation'` for the per-company tab and have no business in the admin-only
   * provisioning queue — they would be shown a door that opens onto Access denied, which is the
   * SUP-251 symptom [ENG-1197].
   *
   * Opt-in on purpose: tightening the default would change the visibility of every existing item.
   */
  roles?: string[];
}

/**
 * An item whose `to` is an absolute URL to another app rather than a route in this one: the sidebar
 * renders it as a plain anchor opening in a new tab, not a `NavLink`.
 *
 * `roles` is required, not optional as it is above, and that is the point of the separate type. The
 * default matcher asks whether an allowed path fragment appears anywhere in `to`; against a host
 * name that question is meaningless — 'access' is a substring of plenty of URLs, and nothing about
 * the ones it misses says the role should not see the item.
 */
interface ExternalMenuItem extends Omit<RouteMenuItem, 'external' | 'roles'> {
  external: true;
  roles: string[];
}

export type MenuItem = RouteMenuItem | ExternalMenuItem;

export const filteredMenu = (
  menuList: MenuItem[],
  adminRole?: string,
): MenuItem[] => {
  // A realm role with no backoffice mapping — 'client', 'sales', or one assigned after this list was
  // written — yields an empty menu rather than throwing on an undefined path list.
  const allowedPaths = adminRole
    ? accessMapping[adminRole as keyof typeof accessMapping]
    : undefined;

  if (!allowedPaths || !adminRole) {
    return [];
  }

  return menuList.filter((item) =>
    item.roles
      ? item.roles.includes(adminRole)
      : allowedPaths.some((allowedPath) => item.to.includes(allowedPath))
  );
};
